import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Search, ShoppingBag, LayoutGrid, FileText } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

export default function Layout({ children, home: homeProp }) {
  const loc = useLocation();
  const { count } = useCart();
  const [home, setHome] = useState(homeProp || null);

  useEffect(() => {
    if (homeProp) { setHome(homeProp); return; }
    api.get("/home").then((r) => setHome(r.data)).catch(() => {});
  }, [homeProp]);
  const nav = [
    { to: "/", label: "Home", icon: Home, testid: "nav-home" },
    { to: "/catalogo", label: "Catalogo", icon: LayoutGrid, testid: "nav-catalog" },
    { to: "/ricerca", label: "Cerca", icon: Search, testid: "nav-search" },
    { to: "/carrello", label: "Carrello", icon: ShoppingBag, testid: "nav-cart", badge: count },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            {home?.logo_url ? (
              <img src={home.logo_url} alt="Spaccatempo" className="h-10 sm:h-12 w-auto object-contain" />
            ) : (
              <>
                <div className="w-9 h-9 rounded-xl bg-brand-blue flex items-center justify-center">
                  <span className="text-brand-yellow font-heading font-black text-lg">S</span>
                </div>
                <div className="leading-tight">
                  <div className="font-heading font-black text-brand-blue text-base">Spaccatempo</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Toys & Collectibles</div>
                </div>
              </>
            )}
          </Link>
          <div className="flex items-center gap-2">
          <Link
            to="/pdf-gratuiti"
            data-testid="header-free-pdfs-link"
            className="h-10 px-3 rounded-full border border-brand-blue/20 text-brand-blue text-xs font-bold inline-flex items-center gap-1 hover:bg-blue-50 transition-colors"
          >
            <FileText className="w-4 h-4" strokeWidth={2.5} /> PDF gratis
          </Link>
          <Link
            to="/carrello"
            data-testid="header-cart-btn"
            className="relative w-10 h-10 rounded-full bg-brand-yellow text-brand-blue flex items-center justify-center hover:bg-brand-yellow-dark transition"
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={2.5} />
            {count > 0 && (
              <span
                data-testid="cart-badge"
                className="absolute -top-1 -right-1 bg-brand-blue text-white text-[10px] font-bold min-w-5 h-5 px-1 rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 pb-24 max-w-6xl w-full mx-auto">{children}</main>

      <footer className="max-w-6xl w-full mx-auto px-4 pb-3 pt-1 text-center text-xs text-slate-400">
        <Link to="/pdf-gratuiti" data-testid="footer-free-pdfs-link" className="hover:text-brand-blue underline">
          PDF gratuiti
        </Link>
        <span className="mx-2">·</span>
        <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-brand-blue underline">
          Privacy Policy
        </Link>
        <span className="mx-2">·</span>
        <span>© Spaccatempo – Toys & Collectibles</span>
      </footer>

      <nav className="fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-white/90 border-t border-border">
        <div className="max-w-6xl mx-auto grid grid-cols-4">
          {nav.map((n) => {
            const active = loc.pathname === n.to || (n.to !== "/" && loc.pathname.startsWith(n.to));
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex flex-col items-center justify-center py-2 min-h-[56px] text-xs relative transition ${
                  active ? "text-brand-blue" : "text-slate-500"
                }`}
              >
                <Icon className="w-5 h-5 mb-0.5" strokeWidth={2.5} />
                <span className="font-medium">{n.label}</span>
                {n.badge > 0 && (
                  <span className="absolute top-1 right-1/4 bg-brand-yellow text-brand-blue text-[10px] font-bold min-w-4 h-4 px-1 rounded-full flex items-center justify-center">
                    {n.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
